import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Trash2,
  RefreshCw,
  AlertTriangle,
  Users,
  Clock,
  BookOpen,
  Layers,
  Film,
  Archive,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from '@/hooks/use-toast'
import {
  useListTrashCourses,
  useRestoreCourse,
  useListTrashModules,
  useRestoreModule,
  useListTrashEpisodes,
  useRestoreEpisode,
} from '@/services/admin/trash.service'
import { getErrorMessage } from '@/utils/error'

export function TrashManagement() {
  const [selectedItem, setSelectedItem] = useState<{
    id: number
    type: 'course' | 'module' | 'episode'
    title: string
  } | null>(null)

  // Queries
  const {
    data: trashCourses = [],
    isLoading: loadingCourses,
    refetch: refetchCourses,
  } = useListTrashCourses()
  const {
    data: trashModules = [],
    isLoading: loadingModules,
    refetch: refetchModules,
  } = useListTrashModules()
  const {
    data: trashEpisodes = [],
    isLoading: loadingEpisodes,
    refetch: refetchEpisodes,
  } = useListTrashEpisodes()

  // Mutations
  const { mutate: restoreCourse, isPending: restoringCourse } = useRestoreCourse()
  const { mutate: restoreModule, isPending: restoringModule } = useRestoreModule()
  const { mutate: restoreEpisode, isPending: restoringEpisode } = useRestoreEpisode()

  const handleRestore = () => {
    if (!selectedItem) return

    const onSuccess = () => {
      toast({
        title: 'Item restaurado!',
        description: `${selectedItem.title} foi restaurado com sucesso.`,
      })
      setSelectedItem(null)

      // Refetch all trash lists
      refetchCourses()
      refetchModules()
      refetchEpisodes()
    }

    const onError = (error: unknown) => {
      toast({
        title: 'Erro ao restaurar',
        description: getErrorMessage(error, 'Ocorreu um erro ao restaurar o item.'),
        variant: 'destructive',
      })
    }

    if (selectedItem.type === 'course') {
      restoreCourse(selectedItem.id, { onSuccess, onError })
    } else if (selectedItem.type === 'module') {
      restoreModule(selectedItem.id, { onSuccess, onError })
    } else if (selectedItem.type === 'episode') {
      restoreEpisode(selectedItem.id, { onSuccess, onError })
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-primary" />
            Lixeira
          </CardTitle>
          <CardDescription>
            Itens deletados podem ser restaurados ou removidos permanentemente após 30 dias
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="courses" className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="courses" className="gap-2">
                <BookOpen className="w-4 h-4" />
                Cursos
                {trashCourses.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {trashCourses.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="modules" className="gap-2">
                <Layers className="w-4 h-4" />
                Módulos
                {trashModules.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {trashModules.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="episodes" className="gap-2">
                <Film className="w-4 h-4" />
                Episódios
                {trashEpisodes.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {trashEpisodes.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Courses Tab */}
            <TabsContent value="courses" className="space-y-4">
              {loadingCourses ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : trashCourses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum curso na lixeira</p>
                </div>
              ) : (
                trashCourses.map((course) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <BookOpen className="w-5 h-5 text-muted-foreground mt-1" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground">{course.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {course.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Deletado {formatDate(course.deletedAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Layers className="w-4 h-4" />
                            {course.totalModulesCount} módulos
                          </div>
                          <div className="flex items-center gap-1">
                            <Film className="w-4 h-4" />
                            {course.totalEpisodesCount} episódios
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {course._count.enrollments} matrículas
                          </div>
                        </div>

                        {course._count.enrollments > 0 && (
                          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500">
                            <AlertTriangle className="w-4 h-4" />
                            <span>
                              {course._count.enrollments} usuário(s) matriculado(s) têm progresso
                              preservado
                            </span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedItem({ id: course.id, type: 'course', title: course.title })
                        }
                        disabled={restoringCourse}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Restaurar
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Modules Tab */}
            <TabsContent value="modules" className="space-y-4">
              {loadingModules ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : trashModules.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum módulo na lixeira</p>
                </div>
              ) : (
                trashModules.map((module) => (
                  <motion.div
                    key={module.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <Layers className="w-5 h-5 text-muted-foreground mt-1" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground">{module.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              Curso: {module.course?.title}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Deletado {formatDate(module.deletedAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Film className="w-4 h-4" />
                            {module.totalEpisodesCount} episódios
                          </div>
                        </div>

                        {module.deletedEpisodesCount > 0 && (
                          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500">
                            <AlertTriangle className="w-4 h-4" />
                            <span>
                              {module.deletedEpisodesCount} episódio(s) também serão restaurados
                            </span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedItem({ id: module.id, type: 'module', title: module.title })
                        }
                        disabled={restoringModule}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Restaurar
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Episodes Tab */}
            <TabsContent value="episodes" className="space-y-4">
              {loadingEpisodes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : trashEpisodes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum episódio na lixeira</p>
                </div>
              ) : (
                trashEpisodes.map((episode) => (
                  <motion.div
                    key={episode.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <Film className="w-5 h-5 text-muted-foreground mt-1" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground">{episode.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {episode.module?.course?.title} → {episode.module?.title}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Deletado {formatDate(episode.deletedAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {episode.userProgressCount} usuário(s) com progresso
                          </div>
                        </div>

                        {episode.userProgressCount > 0 && (
                          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500">
                            <AlertTriangle className="w-4 h-4" />
                            <span>
                              Progresso de {episode.userProgressCount} usuário(s) será preservado
                            </span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedItem({ id: episode.id, type: 'episode', title: episode.title })
                        }
                        disabled={restoringEpisode}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Restaurar
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar item?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Você está prestes a restaurar:</p>
              <p className="font-semibold text-foreground">{selectedItem?.title}</p>
              <p className="text-sm">
                {selectedItem?.type === 'course' &&
                  'Este curso, seus módulos e episódios serão restaurados.'}
                {selectedItem?.type === 'module' &&
                  'Este módulo e seus episódios serão restaurados.'}
                {selectedItem?.type === 'episode' && 'Este episódio será restaurado.'}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
